import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import {
  getCartItemId,
  useCartManager,
} from "@/components/providers/CartProvider";
import * as Dialog from "@radix-ui/react-dialog";
import {
  Button,
  Flex,
  IconButton,
  Text,
  Theme,
  Tooltip,
} from "@radix-ui/themes";
import { useState } from "react";
import { IoMdClose } from "react-icons/io";
import { MdInfoOutline } from "react-icons/md";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

import AuthFlow from "@/components/auth/AuthFlow";
import { usePersonaPickerSuppression } from "@/components/persona-picker/usePersonaPickerVisibility";
import CartItem from "./CartItem";

export default function CartDrawer() {
  const [showAuth, setShowAuth] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const { data } = useLoggedInUser();
  const isAuthenticated = !!data;

  const { isCartOpen, onToggleCart, subtotalPrice, checkoutCart, cartItems } =
    useCartManager();

  usePersonaPickerSuppression("cart-open", isCartOpen);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      setShowAuth(true);
      return;
    }

    askConsentOrCheckout();
  };

  // Between Checkout and payment: buyers who already said yes go straight
  // through; everyone else gets the consent prompt in place of the footer,
  // and either answer proceeds to payment ("no" just isn't remembered).
  const askConsentOrCheckout = () => {
    if (data?.shareContactWithSellers) {
      checkoutCart({ shareContact: true });
      return;
    }
    setShowConsent(true);
  };

  const handleConsent = (shareContact: boolean) => {
    setShowConsent(false);
    checkoutCart({ shareContact });
  };

  const handleAuthSuccess = () => {
    setShowAuth(false);
    askConsentOrCheckout();
  };

  const handleCartOpenChange = (open: boolean) => {
    onToggleCart();
    if (!open) {
      setShowAuth(false);
      setShowConsent(false);
    }
  };

  return (
    <Dialog.Root open={isCartOpen} onOpenChange={handleCartOpenChange}>
      <Dialog.Portal>
        <Theme>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          <Dialog.Content
            data-testid="cart-drawer"
            className="drawer-wrapper fixed right-0 top-0 h-full w-full sm:w-[450px] rounded-l-[15px] bg-white shadow-xl transition-transform data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full"
          >
            <VisuallyHidden>
              <Dialog.Title>My Cart</Dialog.Title>
            </VisuallyHidden>
            <Flex className="flex-col w-full p-[20px] pl-0 pr-0 h-full">
              <Flex className="h-[40px] items-center justify-center w-full pl-[15px] pr-[15px]">
                <Text className="flex-1 font-bold text-[20px]">My Cart</Text>
                <Dialog.Close asChild>
                  <IconButton
                    className="text-[22px] cursor-pointer"
                    variant="ghost"
                    color="gray"
                    radius="full"
                    size="2"
                  >
                    <IoMdClose />
                  </IconButton>
                </Dialog.Close>
              </Flex>

              {showAuth ? (
                /* Inline auth step — items top, auth pinned bottom */
                <>
                  <Flex className="flex-col flex-1 w-full pl-[15px] pr-[15px] overflow-y-auto">
                    {cartItems.map((item) => (
                      <CartItem key={getCartItemId(item)} data={item} />
                    ))}
                  </Flex>

                  <Flex className="flex-col border-t border-gray-200 px-[15px] py-4 overflow-y-auto max-h-[60%]">
                    <AuthFlow
                      initialView="signup"
                      onSuccess={handleAuthSuccess}
                      compact
                      hideHeading
                      codeFirst
                    />
                  </Flex>
                </>
              ) : (
                /* Normal cart view */
                <>
                  {/* Custom always-visible scrollbar: with the OS overlay
                      scrollbars hidden, a full first screen of items read as
                      the whole cart and the rest looked silently dropped
                      (LUT-944). */}
                  <Flex className="flex-col flex-1 w-full pl-[15px] pr-[15px] overflow-y-auto [&::-webkit-scrollbar]:w-[8px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[rgba(5,12,43,0.25)] [&::-webkit-scrollbar-track]:bg-transparent">
                    {cartItems.length === 0 ? (
                      <Flex
                        data-testid="cart-empty-state"
                        className="items-center justify-center h-full"
                      >
                        <Text className="text-text-secondary">
                          Your cart is empty
                        </Text>
                      </Flex>
                    ) : (
                      cartItems.map((item) => (
                        <CartItem key={getCartItemId(item)} data={item} />
                      ))
                    )}
                  </Flex>

                  {showConsent ? (
                    /* Consent step in place of the footer: either answer goes
                       straight to payment — no extra click. */
                    <Flex
                      direction="column"
                      className="border-t-[1px] pt-[15px] pb-[30px] px-[15px] gap-[12px]"
                      data-testid="cart-consent-prompt"
                    >
                      <Flex className="items-center gap-[6px]">
                        <Text className="font-semibold text-[14px]">
                          Can artists you purchase from contact you?
                        </Text>
                        <Tooltip content="If you choose yes, you allow your name and email to be shared with artists you purchase from. You can change this anytime in your settings.">
                          <span className="text-text-tertiary cursor-help">
                            <MdInfoOutline size={16} />
                          </span>
                        </Tooltip>
                      </Flex>
                      <Flex className="gap-[10px]">
                        <Button
                          data-testid="cart-consent-no"
                          onClick={() => handleConsent(false)}
                          size="3"
                          radius="large"
                          variant="soft"
                          color="gray"
                          className="flex-1"
                        >
                          No
                        </Button>
                        <Button
                          data-testid="cart-consent-yes"
                          onClick={() => handleConsent(true)}
                          size="3"
                          radius="large"
                          className="flex-1"
                        >
                          Yes
                        </Button>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex className="border-t-[1px] pt-[15px] pb-[30px] pr-[15px] flex-row justify-end gap-[20px] items-center">
                      <Button
                        data-testid="cart-keep-shopping"
                        onClick={onToggleCart}
                        variant="ghost"
                        color="gray"
                      >
                        Keep Shopping
                      </Button>
                      <Button
                        data-testid="cart-checkout-button"
                        onClick={handleCheckout}
                        disabled={cartItems.length === 0}
                        size="2"
                        radius="full"
                      >
                        {`Checkout ${subtotalPrice > 0 ? `$${subtotalPrice.toFixed(2)}` : ""}`}
                      </Button>
                    </Flex>
                  )}
                </>
              )}
            </Flex>
          </Dialog.Content>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
